import { CdkDragMove, CdkDragRelease, CdkDragStart } from "@angular/cdk/drag-drop";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { LoggingService } from "@merri/common";
import { fromEvent, merge, of, Subject } from "rxjs";
import { catchError, debounceTime, takeUntil, tap } from "rxjs/operators";
import { HelperMethods } from "src/app/shared";
import { DragDropPanelType, Rect, VisionboardItem, VisionboardMode, VisionboardSearchItem } from "../../models";
import {
  VisionboardDragDropService,
  VisionboardService,
  VisionboardVersionsService,
  VisionboardWorkspaceFreeformService,
  VisionboardWorkspaceItemsStoreService,
} from "../../services";

@Component({
    selector: "app-visionboard-workspace-freeform-selected-items",
    templateUrl: "./visionboard-workspace-freeform-selected-items.component.html",
    styleUrls: ["./visionboard-workspace-freeform-selected-items.component.scss"],
    standalone: false
})
export class VisionboardWorkspaceFreeformSelectedItemsComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void>;
  private _hasLoaded: boolean;

  items: VisionboardItem[];
  externalDrag: boolean;
  dragging: boolean;
  allowDrop: boolean;
  hide: boolean;
  header: string;
  dropHereMessage: string;
  hideItems: boolean;
  forceDropHereMessage: boolean;

  constructor(
    private visionboardService: VisionboardService,
    private visionboardDragDropService: VisionboardDragDropService,
    private visionboardVersionsService: VisionboardVersionsService,
    private visionboardWorkspaceItemsStoreService: VisionboardWorkspaceItemsStoreService,
    private visionboardWorkspaceFreeformService: VisionboardWorkspaceFreeformService,
    private matDialog: MatDialog,
  ) {
    this.header = "MY VISION BOARD";
    this.dropHereMessage = "ARRANGE YOUR VISION BOARD HERE";
    this._destroyed$ = new Subject<void>();

    LoggingService.log("VisionboardWorkspaceSelectedItemsComponent - ctor");
  }

  ngOnInit() {
    this.visionboardWorkspaceItemsStoreService.items$.pipe(takeUntil(this._destroyed$)).subscribe(update => {
      if (!update.refresh && this._hasLoaded) {
        LoggingService.log("VisionboardWorkspaceSelectedItemsComponent - items$ - skip - no refresh");
        return;
      }

      if (this.visionboardService.getCurrentMode() !== VisionboardMode.Freeform) {
        LoggingService.log("VisionboardWorkspaceSelectedItemsComponent - items$ - skip - not freeform");
        return;
      }

      this._hasLoaded = true;
      this.hideItems = true;
      this.items = update.store.userItems;

      this.forceDropHereMessage = update.store.failed;
      this.dropHereMessage = this.items ? "ARRANGE YOUR VISION BOARD HERE" : "FAILED TO LOAD VISION BOARD, TRY AGAIN";

      setTimeout(() => {
        this.setHeightAndRecalcItemLocations();
        this.hideItems = false;
      }, 0);
    });

    this.visionboardWorkspaceItemsStoreService.itemAdded$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: () => {
        setTimeout(() => {
          this.setHeightAndRecalcItemLocations();
        }, 0);
      },
    });

    this.visionboardDragDropService.hover$.pipe(takeUntil(this._destroyed$)).subscribe(data => {
      this.dragging = data && !!data.source;
      this.allowDrop = this.dragging && data.destination === DragDropPanelType.WorkspaceSelectedItems;
      this.externalDrag = this.dragging && data.source !== DragDropPanelType.WorkspaceSelectedItems;
    });

    this.visionboardDragDropService.dropped$
      .pipe(
        tap(data => {
          if (data && data.destination === DragDropPanelType.WorkspaceSelectedItems) {
            if (data.source === DragDropPanelType.WorkspaceSelectedItems && data.item instanceof VisionboardItem) {
              this.visionboardWorkspaceFreeformService.setItemLocations(
                data.item,
                data.itemClientRect,
                this.visionboardDragDropService.getWorkspaceSelectedItemsRect(),
              );

              this.visionboardWorkspaceItemsStoreService.save();
            } else if (data.item instanceof VisionboardSearchItem) {
              const clone = data.item.getVisionboardItem();

              this.visionboardWorkspaceFreeformService.setItemLocations(
                clone,
                data.itemClientRect,
                this.visionboardDragDropService.getWorkspaceSelectedItemsRect(),
              );

              this.visionboardWorkspaceItemsStoreService.addItem(clone);
            }
          }
        }),
        catchError(() => of(false)),
        takeUntil(this._destroyed$),
      )
      .subscribe();

    this.visionboardVersionsService.selectedVersion$.pipe(takeUntil(this._destroyed$)).subscribe(version => {
      this.hide = !version;
    });

    merge(fromEvent(window, "resize"), fromEvent(window, "resize").pipe(debounceTime(100)))
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: () => {
          this.setHeightAndRecalcItemLocations();
        },
      });
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();

    LoggingService.log("VisionboardWorkspaceSelectedItemsComponent - destroy");
  }

  onDragStart(event: CdkDragStart, item: VisionboardItem) {
    HelperMethods.moveItemToEndOfArray(this.items, item);

    this.visionboardDragDropService.onDrag(item, DragDropPanelType.WorkspaceSelectedItems);
  }

  onDragReleased(event: CdkDragRelease, item: VisionboardItem) {
    this.visionboardDragDropService.onDrop();
  }

  onDragMoved(event: CdkDragMove, item: VisionboardItem) {
    const rect = event.source.element.nativeElement.getBoundingClientRect();

    const itemRect = new Rect(rect.left, rect.top, rect.width, rect.height);
    this.visionboardDragDropService.onMove(itemRect);
  }

  trackWorkspaceItemsFn(index: number, item: VisionboardItem): any {
    return item.internalId;
  }

  onItemRemoved(item: VisionboardItem) {
    this.visionboardService
      .confirmItemDeleteOrReplace$(this.matDialog)
      .pipe(
        tap(result => {
          if (result.result) {
            this.visionboardWorkspaceItemsStoreService.removeItem(item, false);
          }
        }),
      )
      .subscribe();
  }

  private setHeightAndRecalcItemLocations() {
    this.visionboardWorkspaceFreeformService.recalcItemLocations(
      this.items,
      this.visionboardDragDropService.getWorkspaceSelectedItemsRect(),
    );
    this.setDropHereWidth();
  }

  private setDropHereWidth() {
    const dropHere = document.getElementById("visionboard-workspace-selected-items-drop-here");
    const items = document.getElementById("visionboard-workspace-selected-items");

    if (dropHere && items) {
      dropHere.style.width = `${items.getBoundingClientRect().width - 10}px`;
    }
  }
}
